import React from 'react';
import { useGetPromotersQuery, useCountPromotersQuery } from '../promoter.service';
import Pagination from '../../../components/pagination';
import usePagination from '../../../customHooks/usePagination';
import { Promoter } from '../types/promoter.entity';
import { PromotersAutoSearch } from './PromotersAutoSearch';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaExternalLinkAlt, FaPlus } from 'react-icons/fa';
import { PromotersTableSkeleton } from './PromotersTableSkeleton';
import { appendSearchToPath, usePaginatedTableSearchParams } from 'src/customHooks/usePaginatedTableSearchParams';

export const PromotersList: React.FC = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const tableSearch = usePaginatedTableSearchParams<{
    id?: string;
    name?: string;
    email?: string;
  }>({
    parseState: (searchParams) => {
      const query: { id?: string; name?: string; email?: string } = {};
      const id = searchParams.get('id');
      const name = searchParams.get('name');
      const email = searchParams.get('email');

      if (id) query.id = id;
      if (name) query.name = name;
      if (email) query.email = email;

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.id) nextParams.set('id', state.id);
      if (state.name) nextParams.set('name', state.name);
      if (state.email) nextParams.set('email', state.email);
      return nextParams;
    },
  });
  const { data, numberOfPages, currentPage, next, prev, isError, error, isLoading, isFetching } =
    usePagination<Promoter>({
      useGetDataQuery: useGetPromotersQuery,
      useCountData: useCountPromotersQuery,
      initQuery: tableSearch.state,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const { data: totalCount } = useCountPromotersQuery({});
  const showTableSkeleton = !data && (isLoading || isFetching);
  
  if (isError) {
    return (
      <div className="text-red-500 p-4 bg-red-50 border border-red-200 rounded-lg max-w-4xl mx-auto">
        <h3 className="font-semibold text-lg mb-2">Error Loading Promoters</h3>
        <p className="mb-3">{error}</p>
        <div className="flex gap-2">
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleCreatePromoter = () => {
    navigate(appendSearchToPath('/promoters/create', search));
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <div className="container mx-auto px-4 antialiased">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 mb-4 sm:mb-0">Promoters Management</h1>
        <button
          onClick={handleCreatePromoter}
          className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2 flex items-center gap-2"
        >
          <FaPlus className="text-sm" />
          Add Promoter
        </button>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex justify-between items-center">
          <span className="text-gray-700">
            Total Promoters: {totalCount || 0}
          </span>
          <span className="text-gray-500 text-sm">
            Page {currentPage} of {numberOfPages}
          </span>
        </div>
        
        <PromotersAutoSearch updateQuery={tableSearch.replaceFilters} initialQuery={tableSearch.state} />
        
        <div className="overflow-x-auto mb-6 mt-4">
          <table className="min-w-full border border-gray-200 divide-y divide-gray-200 text-sm text-slate-700">
            <thead className="bg-gray-800 text-white text-center">
              <tr>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Promoter ID</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Name</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Email</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Commission Rate</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Total Earnings</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Created Date</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider font-semibold">Actions</th>
              </tr>
            </thead>

            <tbody>
              {showTableSkeleton ? (
                <PromotersTableSkeleton rows={10} />
              ) : data && data.length === 0 ? (
                <tr className="text-center">
                  <td colSpan={7} className="px-4 py-3">
                    No Promoters Found
                  </td>
                </tr>
              ) : (
                data && data.map((promoter) => (
                <tr key={promoter.id} className="text-center odd:bg-white even:bg-gray-50 hover:bg-gray-100">
                  <td className="px-4 py-3 border-t border-gray-200 max-w-xs whitespace-nowrap overflow-hidden text-ellipsis font-mono text-[11px] tracking-wide text-slate-800">
                    {promoter.id}
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 max-w-xs whitespace-nowrap overflow-hidden text-ellipsis font-medium text-slate-900">
                    {promoter.name}
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 text-slate-600">
                    {promoter.email || '-'}
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200">
                    <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full border border-blue-200">
                      {promoter.commissionRate + '%'}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 font-medium text-slate-900">
                    {formatCurrency(promoter.totalEarningsInEuros, 'EUR')}
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200 text-slate-600">
                    {promoter.createdAt ? new Date(promoter.createdAt.seconds * 1000).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-4 py-3 border-t border-gray-200">
                    <Link
                      to={appendSearchToPath(`/promoters/${promoter.id}`, search)}
                      className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:ring-offset-2"
                    >
                      <FaExternalLinkAlt />
                    </Link>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        <Pagination
          currentPage={currentPage}
          numberOfPages={numberOfPages || 1}
          prev={prev}
          next={next}
          isLoading={isLoading || isFetching}
        />
      </div>
    </div>
  );
};
